const UTF8_BOM = "\uFEFF";

/**
 * Characters that could trigger formula execution in spreadsheet apps
 */
const FORMULA_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Escape a field value for CSV per RFC 4180
 */
function escapeCSVField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }

  let str = String(value);

  // Prevent formula injection
  if (FORMULA_PREFIXES.some((prefix) => str.startsWith(prefix))) {
    str = "'" + str;
  }

  // Check if we need to quote the field
  const needsQuoting =
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r");

  if (needsQuoting) {
    // Escape double quotes by doubling them
    str = str.replace(/"/g, '""');
    // Wrap in double quotes
    str = `"${str}"`;
  }

  return str;
}

/**
 * Column ID to Portuguese header name mapping
 */
const columnHeaders: Record<string, string> = {
  store: "Loja",
  docCode: "Documento",
  register: "Caixa",
  nsu: "NSU",
  value: "Valor",
  terminalDate: "Data Terminal",
  status: "Status",
  processingResult: "Mensagem de Erro",
  processingDate: "Data Processamento",
  requestingTerminal: "Terminal",
  planId: "ID Plano",
  planDescription: "Descricao Plano",
  softDescriptor: "Soft Descriptor",
  networkOrigin: "Rede Origem PDV",
  adminInstallment: "Parcelado Admin",
  cardholderId: "ID Portador",
  registerStatus: "Status Registro",
  createdAt: "Criado Em",
  lastUpdated: "Atualizado Em",
};

/**
 * Status to Portuguese label mapping
 */
const statusLabels: Record<string, string> = {
  pending: "Pendente",
  successful: "Processado",
  failed: "Com Erro",
};

interface ExportRow {
  store?: number | null;
  docCode?: number | null;
  register?: number | null;
  nsu?: string | null;
  value?: number | null;
  terminalDate?: string | null;
  status?: string | null;
  processingResult?: string | null;
  processingDate?: string | null;
  requestingTerminal?: string | null;
  planId?: string | null;
  planDescription?: string | null;
  softDescriptor?: string | null;
  networkOrigin?: string | null;
  adminInstallment?: string | null;
  cardholderId?: string | null;
  registerStatus?: number | null;
  createdAt?: string | null;
  lastUpdated?: string | null;
}

/**
 * Format a row value for CSV export
 */
function formatValue(
  columnId: string,
  value: unknown
): string | number | null {
  if (value == null) return null;

  const formatters: Record<string, () => string | number | null> = {
    status: () => statusLabels[value as string] || String(value),
    value: () => (typeof value === "number" ? value : null),
    terminalDate: () => String(value),
    processingDate: () => String(value),
    createdAt: () => String(value),
    lastUpdated: () => String(value),
  };

  return formatters[columnId]?.() ?? String(value);
}

/**
 * Generate CSV content from data
 */
export function generateCSV(data: ExportRow[], columns: string[]): string {
  const lines: string[] = [];

  // Header row
  const headerRow = columns.map((col) =>
    escapeCSVField(columnHeaders[col] || col)
  );
  lines.push(headerRow.join(","));

  // Data rows
  for (const row of data) {
    const rowValues = columns.map((col) => {
      const value = row[col as keyof ExportRow];
      const formatted = formatValue(col, value);
      return escapeCSVField(formatted);
    });
    lines.push(rowValues.join(","));
  }

  // Join with CRLF per RFC 4180
  return UTF8_BOM + lines.join("\r\n");
}

/**
 * Generate filename with current date
 */
export function generateFilename(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `cav-transacoes-${year}-${month}-${day}.csv`;
}

/**
 * Trigger file download
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generic file download helper
 */
export function downloadFile(
  content: string,
  filename: string,
  mimeType = "text/csv;charset=utf-8"
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Generate template CSV for import
 */
export function generateTemplateCSV(): string {
  const headers = "CAVNUM_LOJA,CAVDOCCOD";
  return UTF8_BOM + headers + "\n";
}

/**
 * Generate CSV for skipped transactions from import results
 */
export function generateSkippedCSV(
  skipped: Array<{ store: number; docCode: number; reason: string }>,
  reasonLabels: Record<string, string>
): string {
  const header = "CAVNUM_LOJA,CAVDOCCOD,MOTIVO";
  const rows = skipped.map(
    (item) => `${item.store},${item.docCode},${reasonLabels[item.reason]}`
  );
  return UTF8_BOM + [header, ...rows].join("\r\n");
}
